import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { PinnacleTextField } from "../../components/PinnacleTextField";
import getTheme from "../../theme/theme";
import { PinnacleThemes } from "../../theme/types";
import { handleApiError } from "../../utils/errorHandler";
import { E_CONFIRM_PASSWORD, E_ENTER_PASSWORD, E_PASSWORD_NOT_MATCHED, PREBOARD_AUTH_BANNER_DESKTOP_URL, PREBOARD_AUTH_BANNER_MOBILE_URL, PREBOARD_LOGO_WITH_TEXT_URL } from "../../utils/constants";
import PasswordAdornment from "../../components/common/PasswordAdornment/PasswordAdornment";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { blue, grey } from "@mui/material/colors";
import { resetPassword } from "../../api/services/auth";

export default function ResetPasswordPage() {
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const expireToken = searchParams.get("expire");

    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [formDisabled, setFormDisabled] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const [confirmPasswordTouched, setConfirmPasswordTouched] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const isPasswordValid = password.length > 0;
    const isConfirmPasswordValid = confirmPassword.length > 0;

    const handleTogglePassword = () => setShowPassword((prev) => !prev);
    const handleToggleConfirmPassword = () => setShowConfirmPassword((prev) => !prev);

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        if (!passwordTouched) setPasswordTouched(true);
        if (errorMessage) setErrorMessage(null);
    };

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(e.target.value);
        if (!confirmPasswordTouched) setConfirmPasswordTouched(true);
        if (errorMessage) setErrorMessage(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordTouched(true);

        if (!isPasswordValid || !isConfirmPasswordValid) {
            return;
        }

        setFormDisabled(true);
        try {
            const resetPasswordResponse = await resetPassword(password, expireToken);
            pushNotification({
                details: resetPasswordResponse.message,
                type: NotificationTypes.Success,
            });
            navigate("/login");
        } catch (error) {
            const { errorMessage } = handleApiError(error);
            pushNotification({
                details: errorMessage,
                type: NotificationTypes.Error,
            });
        } finally {
            setFormDisabled(false);
        }
    };

    return (
        <Grid container sx={{ minHeight: '100vh', flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Left image panel */}
            <Grid
                size={{
                    xs: 12,
                    md: 5
                }}
                sx={{
                    height: { xs: '200px', md: 'auto' },
                    overflow: "hidden",
                    backgroundImage: {
                        xs: `url(${PREBOARD_AUTH_BANNER_MOBILE_URL})`,
                        md: `url(${PREBOARD_AUTH_BANNER_DESKTOP_URL})`
                    },
                    backgroundRepeat: "no-repeat",
                    backgroundSize: { xs: 'cover', md: 'contain' },
                    backgroundPosition: "left top",
                    backgroundColor: "#fff",
                }}
            />

            {/* Right form panel */}
            <Grid
                size={{
                    xs: 12,
                    md: 7
                }}
                sx={{
                    display: 'flex',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    alignItems: 'center',
                    placeSelf: 'center',
                    overflowY: 'auto',
                    p: { xs: 2, md: 5 },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: { xs: 'center', md: 'flex-start' },
                        alignItems: { xs: 'center', md: 'flex-start' },
                        width: '100%',
                        minHeight: '100%',
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: "450px",
                            minWidth: "300px",
                            width: "90%",
                            borderRadius: "5px",
                            boxSizing: "border-box",
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                        }}
                    >
                        <img
                            src={PREBOARD_LOGO_WITH_TEXT_URL}
                            alt='pinnacle-logo'
                            style={{
                                width: "300px",
                                height: "auto",
                                alignSelf: "left",
                            }}
                        />
                        <Typography variant="h3" gutterBottom sx={{ mt: 3 }}>
                            Enter New Password
                        </Typography>

                        <form onSubmit={handleSubmit} noValidate>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                                {/* NEW PASSWORD */}
                                <PinnacleTextField
                                    error={passwordTouched && (!isPasswordValid || !!errorMessage)}
                                    color={passwordTouched && (!isPasswordValid || !!errorMessage) ? 'error' : 'primary'}
                                    helperText={
                                        passwordTouched && !isPasswordValid
                                            ? E_ENTER_PASSWORD
                                            : errorMessage
                                    }
                                    id="newPassword"
                                    label="New Password"
                                    variant="outlined"
                                    onChange={handlePasswordChange}
                                    onBlur={() => setPasswordTouched(true)}
                                    value={password}
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    disabled={formDisabled}
                                    slotProps={{
                                        input: {
                                            endAdornment: <PasswordAdornment show={showPassword} toggleShow={handleTogglePassword} />,
                                        },
                                    }}
                                />

                                {/* CONFIRM PASSWORD */}
                                <PinnacleTextField
                                    error={
                                        (confirmPasswordTouched && (!isConfirmPasswordValid || !!errorMessage)) ||
                                        password !== confirmPassword
                                    }
                                    color={
                                        (confirmPasswordTouched && (!isConfirmPasswordValid || !!errorMessage)) ||
                                            password !== confirmPassword
                                            ? 'error'
                                            : 'primary'
                                    }
                                    helperText={
                                        confirmPasswordTouched && !isConfirmPasswordValid
                                            ? E_CONFIRM_PASSWORD
                                            : password !== confirmPassword
                                                ? E_PASSWORD_NOT_MATCHED
                                                : errorMessage
                                    }
                                    id="confirmNewPassword"
                                    label="Confirm New Password"
                                    variant="outlined"
                                    onChange={handleConfirmPasswordChange}
                                    onBlur={() => setConfirmPasswordTouched(true)}
                                    value={confirmPassword}
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    autoComplete="current-confirm-password"
                                    disabled={formDisabled}
                                    slotProps={{
                                        input: {
                                            endAdornment: <PasswordAdornment show={showConfirmPassword} toggleShow={handleToggleConfirmPassword} />,
                                        },
                                    }}
                                />
                            </Box>

                            <Box sx={{ mt: 2 }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    type="submit"
                                    disabled={!isPasswordValid || formDisabled}
                                    sx={{ mb: 1.5 }}
                                >
                                    {formDisabled ? (
                                        <CircularProgress size={24} sx={{ color: blue[300] }} />
                                    ) : (
                                        "SUBMIT"
                                    )}
                                </Button>
                                <Button
                                    fullWidth
                                    variant="text"
                                    style={{
                                        color: "#373737",
                                        textDecoration: "none",
                                        fontWeight: 500
                                    }}
                                    onClick={() => navigate("/login")}
                                >
                                    GO BACK
                                </Button>
                            </Box>
                        </form>
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
}